import { alpha, type Theme } from '@mui/material/styles'
import parsePhoneNumber from 'libphonenumber-js'
import * as yup from 'yup'

import {
  EAgentKeys,
  EAgentStatus,
  TAgent,
  TCreateAssistant,
} from '~/features/agents/models'
import { VOICE_OPTIONS } from '~/features/settings/constants'
import {
  EAgentLanguage,
  EEmailStatus,
  ELoopingAudio,
  EOrchestratorType,
  EPhoneStatus,
  ETextStatus,
} from '~/features/settings/models'
import { EServerPath } from '~/models/api'
import {
  EAgentNotifyWhen,
  ELocationTypeValues,
  ENotificationType,
  EPersonalityValues,
  EScheduleValues,
  ETextMethodValues,
} from '~/models/features'

export const queryKeys = {
  createAgent: () => [EServerPath.agents, 'create'],
  adminAgents: (filters: object = {}) => [EServerPath.adminAgents, filters],
  agents: (filters: object = {}) => [EServerPath.agents, filters],
  guestAgent: (agentId: string) => [EServerPath.guestAgent, agentId],
  agentsByOrganization: (organizationId: string) => [
    queryKeys.agents({ organizationId }),
  ],
  recordIntro: (agentId: string) => [EServerPath.agentsIntro, agentId],
  chatbotSettings: (agentId: string) => [
    EServerPath.chatbotSettings,
    { agentId },
  ],
  checkIfFriendlyUrlIsAvailable: ['checkIfFriendlyUrlIsAvailable'],
  agentsStatus: [EServerPath.agentsStatus],
  orgPhoneNumbers: (organizationId: string, accessToken?: string) =>
    [
      'orgPhoneNumbers',
      organizationId,
      ...(accessToken ? [accessToken] : []),
    ] as const,
  agentExternalIdsByOrganization: (organizationId: string) => [
    'agentExternalIds',
    organizationId,
  ],
  locations: (organizationId: string) => [
    EServerPath.locations,
    organizationId,
  ],
  locationsIntegrationMapping: (organizationId: string, provider?: string) => [
    EServerPath.locationsIntegrationMapping,
    organizationId,
    provider,
  ],
  promptMetadata: (filters: object = {}) => [
    EServerPath.promptMetadata,
    filters,
  ],
  schedules: (organizationId: string) => [
    EServerPath.schedules,
    organizationId,
  ],
  agentSchedule: (agentId: string, purposeKey: string) => [
    EServerPath.agentSchedule,
    agentId,
    purposeKey,
  ],
  agentPhonePrompt: (agentId: string) => [
    EServerPath.agentPhonePrompt,
    agentId,
  ],
  transferStatuses: (organizationId: string) => [
    EServerPath.agentsTransfers,
    organizationId,
  ],
}

export const getDefaultAgentData = (existingAgentData?: TAgent): TAgent => {
  const agent = existingAgentData || ({} as TAgent)

  return {
    ...agent,
    [EAgentKeys.id]: agent[EAgentKeys.id],
    [EAgentKeys.agentEmail]: agent[EAgentKeys.agentEmail] || null,
    [EAgentKeys.emailStatus]:
      agent[EAgentKeys.emailStatus] || EEmailStatus.active,
    [EAgentKeys.agentName]: agent[EAgentKeys.agentName] || '',
    [EAgentKeys.orgId]: agent[EAgentKeys.orgId] || '',
    [EAgentKeys.Org]: agent[EAgentKeys.Org],
    [EAgentKeys.businessName]: agent[EAgentKeys.businessName] || '',
    [EAgentKeys.service]: agent[EAgentKeys.service] || '',
    [EAgentKeys.siteLink]: agent[EAgentKeys.siteLink] || '',
    [EAgentKeys.locationType]:
      agent[EAgentKeys.locationType] || ELocationTypeValues.anywhere,
    [EAgentKeys.location]: agent[EAgentKeys.location] || '',
    [EAgentKeys.pricing]: agent[EAgentKeys.pricing] || '',
    [EAgentKeys.questions]: agent[EAgentKeys.questions] || [],
    [EAgentKeys.facts]: agent[EAgentKeys.facts] || [],
    [EAgentKeys.calendlyLink]: agent[EAgentKeys.calendlyLink] || '',
    [EAgentKeys.cta]: agent[EAgentKeys.cta],
    [EAgentKeys.ctaText]: agent[EAgentKeys.ctaText] || null,
    [EAgentKeys.customURL]: agent[EAgentKeys.customURL] || [],
    [EAgentKeys.emailConsent]: agent[EAgentKeys.emailConsent] || '',
    [EAgentKeys.mobileConsent]: agent[EAgentKeys.mobileConsent] || false,
    [EAgentKeys.notifyEmail]: agent[EAgentKeys.notifyEmail] || '',
    [EAgentKeys.notifyMobile]: agent[EAgentKeys.notifyMobile] || '',
    [EAgentKeys.notifyType]:
      agent[EAgentKeys.notifyType] === null
        ? null
        : agent[EAgentKeys.notifyType] || ENotificationType.email,
    [EAgentKeys.notifyWhen]:
      agent[EAgentKeys.notifyWhen] || EAgentNotifyWhen.afterAllChats,
    [EAgentKeys.personality]:
      agent[EAgentKeys.personality] || EPersonalityValues.professional,
    [EAgentKeys.phoneIntro]: agent[EAgentKeys.phoneIntro] || '',
    [EAgentKeys.phoneNumber]: agent[EAgentKeys.phoneNumber] || '',
    [EAgentKeys.agentLanguage]:
      agent[EAgentKeys.agentLanguage] || EAgentLanguage.English,
    [EAgentKeys.logoImgUrl]: agent[EAgentKeys.logoImgUrl] || null,
    [EAgentKeys.scheduleType]:
      agent[EAgentKeys.scheduleType] || EScheduleValues.askForAvailability,
    [EAgentKeys.zapierId]: agent[EAgentKeys.zapierId] || '',
    [EAgentKeys.customLinks]: agent[EAgentKeys.customLinks] || [],
    [EAgentKeys.chatIntro]: agent[EAgentKeys.chatIntro] || '',
    [EAgentKeys.chatbotImgURL]: agent[EAgentKeys.chatbotImgURL] || undefined,
    [EAgentKeys.showLibby]: agent[EAgentKeys.showLibby] || false,
    [EAgentKeys.showBranding]: agent[EAgentKeys.showBranding] || false,
    [EAgentKeys.chatFriendlyUrl]: agent[EAgentKeys.chatFriendlyUrl] || '',
    [EAgentKeys.phoneStatus]:
      agent[EAgentKeys.phoneStatus] || EPhoneStatus.active,
    [EAgentKeys.phoneVoice]:
      agent[EAgentKeys.phoneVoice] || VOICE_OPTIONS[0].id,
    [EAgentKeys.phoneIntroRecording]:
      agent[EAgentKeys.phoneIntroRecording] || '',
    [EAgentKeys.textMethod]:
      agent[EAgentKeys.textMethod] || ETextMethodValues.SMS,
    [EAgentKeys.scrapeStatus]: agent[EAgentKeys.scrapeStatus] || undefined,
    [EAgentKeys.customInstructions]:
      agent[EAgentKeys.customInstructions] || null,
    [EAgentKeys.escalations]: agent[EAgentKeys.escalations] || [],
    [EAgentKeys.chatStatus]: agent[EAgentKeys.chatStatus] || undefined,
    [EAgentKeys.textStatus]:
      agent[EAgentKeys.textStatus] || ETextStatus.disabled,
    [EAgentKeys.loopingAudio]:
      agent[EAgentKeys.loopingAudio] || ELoopingAudio.off,
    [EAgentKeys.createdDate]: agent.createdDate || undefined,
    [EAgentKeys.updatedDate]: agent.updatedDate || undefined,
    [EAgentKeys.agentStatus]:
      agent[EAgentKeys.agentStatus] || EAgentStatus.active,
  }
}

export const getStringFromAgentFacts = (facts: TAgent['facts']): string[] => {
  return (facts || []).map((fact) => {
    if (typeof fact === 'string') {
      return fact
    }

    return fact.text
  })
}

export function getFormattedPhoneNumber(phoneNumberString: string) {
  const phoneNumber = parsePhoneNumber(phoneNumberString)

  const cleaned = `${phoneNumberString}`.replace(/\D/g, '')
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)

  if (match) {
    return `(${match[2]}) ${match[3]}-${match[4]}`
  }

  if (phoneNumber) {
    return phoneNumber.formatInternational()
  }

  return phoneNumber || ''
}

export const createAssistantValidationSchema = yup.object<TCreateAssistant>({
  [EAgentKeys.agentReferenceName]: yup
    .string()
    .required('Reference name is required'),
  [EAgentKeys.businessName]: yup.string().required('Business name is required'),
  [EAgentKeys.service]: yup.string().required('Service is required'),
  [EAgentKeys.locationId]: yup.string().required('Location is required'),
})

export const getFirstActiveAgent = (agents: TAgent[]): TAgent | undefined => {
  return agents.find((agent) => agent.agentStatus === EAgentStatus.active)
}

export const getAgentDisplayName = (agent: TAgent) =>
  agent[EAgentKeys.agentReferenceName] || agent[EAgentKeys.businessName] || ''

export const sortAgentsBySelectedAndName = ({
  agents,
  selectedAgentId,
}: {
  agents: TAgent[]
  selectedAgentId: string
}) =>
  [...agents].sort((firstAgent, secondAgent) => {
    const firstAgentId = firstAgent[EAgentKeys.id]
    const secondAgentId = secondAgent[EAgentKeys.id]

    if (firstAgentId === selectedAgentId && secondAgentId !== selectedAgentId) {
      return -1
    }

    if (secondAgentId === selectedAgentId && firstAgentId !== selectedAgentId) {
      return 1
    }

    return getAgentDisplayName(firstAgent).localeCompare(
      getAgentDisplayName(secondAgent),
      undefined,
      { sensitivity: 'base' },
    )
  })

export const isAgentActive = (agent: TAgent | undefined): boolean => {
  return agent?.agentStatus === EAgentStatus.active
}

export const getActiveAgents = (agents?: TAgent[] | null): TAgent[] => {
  if (!agents?.length) {
    return []
  }

  return agents.filter(isAgentActive)
}

export const getAgentChatSettings = (
  chatSettings?: TAgent[EAgentKeys.chatSettings],
) =>
  !!chatSettings && Array.isArray(chatSettings) ? chatSettings[0] : chatSettings

export function orchestrationLabel(type: string): string {
  if (type === EOrchestratorType.legacy) return 'Legacy'
  if (type === EOrchestratorType.standard) return 'New'
  if (type === EOrchestratorType.realtime) return 'Realtime'
  return type || '—'
}

export function parseOrchestrator(type: string): EOrchestratorType | null {
  if (type === EOrchestratorType.legacy) return EOrchestratorType.legacy
  if (type === EOrchestratorType.standard) return EOrchestratorType.standard
  if (type === EOrchestratorType.realtime) return EOrchestratorType.realtime
  return null
}

export function orchestratorAccent(
  theme: Theme,
  type: EOrchestratorType | null,
): { color: string; bg: string } {
  if (type === EOrchestratorType.standard)
    return {
      color: theme.palette.success.main,
      bg: alpha(theme.palette.success.main, 0.08),
    }
  if (type === EOrchestratorType.legacy || type === EOrchestratorType.realtime)
    return {
      color: theme.palette.error.main,
      bg: alpha(theme.palette.error.main, 0.06),
    }
  return { color: theme.palette.text.secondary, bg: 'transparent' }
}
